import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'preact/hooks';
import Pubnub from 'pubnub';
import { usePubNub } from 'pubnub-react';

import FeedItem from '@/components/CommunityFeed/FeedItem';
import { FeedMessageWithMeta } from '@/components/CommunityFeed/types';
import Loader from '@/components/Loader';

const CHANNEL = 'communityUpdates';

const queryFn = async (pubnub: Pubnub) => {
  const resp = await pubnub.fetchMessages({
    channels: [CHANNEL],
    count: 3,
  });
  return resp.channels?.[CHANNEL].reverse() as FeedMessageWithMeta[];
};

const CommunityFeedCardList = () => {
  const pubnub = usePubNub();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    // Disabled pubnub as deps since it is not serializable and it should be stable for the lifetime of the component
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['community-feed-card-list'],
    queryFn: () => queryFn(pubnub),
    enabled: !!pubnub,
    // data will be updated through pubnub listener
    staleTime: Infinity,
  });

  // subscribe to new messages and update the query data directly
  useEffect(() => {
    const handler: Pubnub.ListenerParameters['message'] = (event) => {
      if (event.channel !== CHANNEL) return;
      queryClient.setQueryData<typeof data>(['community-feed-card-list'], (oldData) => {
        return oldData ? [event, ...oldData].slice(0, 3) : [event];
      });
    };
    pubnub.addListener({ message: handler });
    pubnub.subscribe({ channels: [CHANNEL] });
    return () => {
      pubnub.unsubscribe({ channels: [CHANNEL] });
      pubnub.removeListener({ message: handler });
    };
  }, [pubnub, queryClient]);

  return isLoading ? (
    <Loader width="40px" height="40px" className="h-[360px]" />
  ) : (
    <ul className="preflight preflight-ul divide-y pb-1">
      {data?.map((message) => (
        <li key={message.timetoken.toString()} className="preflight animate-in animate-out fade-in-50 fade-out">
          <FeedItem message={message} />
        </li>
      ))}
    </ul>
  );
};

export default CommunityFeedCardList;
